import { useRef, useState, useEffect, useCallback } from 'react';

import { useAuthStore } from '@/stores/auth.js';
import type {
  DlqStatsEvent,
  WebSocketEvent,
  WebSocketTopic,
  DlqMessageEvent,
  JobProgressEvent,
  MappingChangeEvent,
  WebSocketEventType,
  PipelineStatusEvent,
  PipelineLogBatchEvent,
  PipelineLogStatusEvent,
  WebSocketConnectionStatus,
  ConnectionEstablishedEvent,
} from '@/types/api';

type EventHandler<T = unknown> = (event: WebSocketEvent<T>) => void;
type EventHandlers = Partial<Record<WebSocketEventType, EventHandler>>;

interface UseWebSocketOptions {
  autoConnect?: boolean;
  reconnectDelay?: number;
  topics?: WebSocketTopic[];
  onDisconnect?: () => void;
  reconnectAttempts?: number;
  onError?: (error: Event) => void;
  onDlqStats?: (event: WebSocketEvent<DlqStatsEvent>) => void;
  onDlqMessage?: (event: WebSocketEvent<DlqMessageEvent>) => void;
  onJobProgress?: (event: WebSocketEvent<JobProgressEvent>) => void;
  onLogBatch?: (event: WebSocketEvent<PipelineLogBatchEvent>) => void;
  onLogStatus?: (event: WebSocketEvent<PipelineLogStatusEvent>) => void;
  onMappingChange?: (event: WebSocketEvent<MappingChangeEvent>) => void;
  onConnect?: (event: WebSocketEvent<ConnectionEstablishedEvent>) => void;
  onPipelineStatus?: (event: WebSocketEvent<PipelineStatusEvent>) => void;
}

interface UseWebSocketReturn {
  connect: () => void;
  isConnected: boolean;
  disconnect: () => void;
  clientId: string | null;
  send: (message: unknown) => void;
  status: WebSocketConnectionStatus;
  subscribe: (topics: WebSocketTopic[]) => void;
  unsubscribe: (topics: WebSocketTopic[]) => void;
}

const getWebSocketUrl = (): string => {
  const host = globalThis.location.host;
  const protocol = globalThis.location.protocol === 'https:' ? 'wss:' : 'ws:';

  return `${protocol}//${host}/api/ws/events`;
};

/**
 * Hook to manage WebSocket connection with automatic reconnection and event handling.
 *
 * @param { UseWebSocketOptions } options Configuration options for connection behavior.
 * @returns { UseWebSocketReturn } Returns connection state and control functions.
 */
export const useWebSocket = (options: UseWebSocketOptions = {}): UseWebSocketReturn => {
  const {
    onError,
    onConnect,
    onLogBatch,
    onDlqStats,
    onLogStatus,
    onDisconnect,
    onDlqMessage,
    onJobProgress,
    onMappingChange,
    onPipelineStatus,
    topics = ['all'],
    autoConnect = true,
    reconnectAttempts = 5,
    reconnectDelay = 3000,
  } = options;

  const [status, setStatus] = useState<WebSocketConnectionStatus>('disconnected');
  const [clientId, setClientId] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldReconnectRef = useRef(true);
  const isMountedRef = useRef(false);
  const topicsRef = useRef(topics);
  topicsRef.current = topics;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onDisconnectRef = useRef(onDisconnect);
  onDisconnectRef.current = onDisconnect;

  const eventHandlersRef = useRef<EventHandlers>({});
  useEffect(() => {
    eventHandlersRef.current = {
      'dlq.stats.updated': onDlqStats as EventHandler,
      'pipeline.log.batch': onLogBatch as EventHandler,
      'dlq.message.added': onDlqMessage as EventHandler,
      'dlq.message.purged': onDlqMessage as EventHandler,
      'mapping.updated': onMappingChange as EventHandler,
      'pipeline.log.status': onLogStatus as EventHandler,
      'connection.established': onConnect as EventHandler,
      'dlq.message.retried': onDlqMessage as EventHandler,
      'job.status.changed': onJobProgress as EventHandler,
      'mapping.activated': onMappingChange as EventHandler,
      'job.progress.updated': onJobProgress as EventHandler,
      'mapping.deactivated': onMappingChange as EventHandler,
      'pipeline.status.changed': onPipelineStatus as EventHandler,
    };
  }, [onConnect, onDlqMessage, onDlqStats, onLogBatch, onLogStatus, onPipelineStatus, onJobProgress, onMappingChange]);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data) as WebSocketEvent;
      const handler = eventHandlersRef.current[data.type];

      if (handler) {
        handler(data);
      }

      if (data.type === 'connection.established') {
        const connectionData = data.data as ConnectionEstablishedEvent;
        setClientId(connectionData.clientId);
      }
    } catch {
      // Ignore parse errors
    }
  }, []);

  const connectRef = useRef<() => void>(() => {});

  const attemptReconnect = useCallback(() => {
    if (!shouldReconnectRef.current) {
      return;
    }

    if (reconnectCountRef.current >= reconnectAttempts) {
      setStatus('disconnected');

      return;
    }

    setStatus('reconnecting');
    reconnectCountRef.current++;

    reconnectTimeoutRef.current = setTimeout(() => {
      if (shouldReconnectRef.current) {
        connectRef.current();
      }
    }, reconnectDelay);
  }, [reconnectAttempts, reconnectDelay]);

  const connect = useCallback(() => {
    if (!isMountedRef.current) {
      return;
    }

    if (useAuthStore.getState().sessionExpired) {
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    clearReconnectTimeout();
    setStatus('connecting');
    shouldReconnectRef.current = true;

    try {
      const ws = new WebSocket(getWebSocketUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        if (!isMountedRef.current) {
          ws.close(1000, 'Component unmounted');

          return;
        }

        setStatus('connected');
        reconnectCountRef.current = 0;

        if (topicsRef.current.length > 0) {
          ws.send(
            JSON.stringify({
              action: 'subscribe',
              topics: topicsRef.current,
            }),
          );
        }
      };

      ws.onmessage = handleMessage;

      ws.onerror = (error) => {
        onErrorRef.current?.(error);
      };

      ws.onclose = () => {
        if (!isMountedRef.current) {
          return;
        }

        setStatus('disconnected');
        setClientId(null);
        wsRef.current = null;
        onDisconnectRef.current?.();

        if (shouldReconnectRef.current) {
          attemptReconnect();
        }
      };
    } catch {
      setStatus('disconnected');
      attemptReconnect();
    }
  }, [handleMessage, attemptReconnect, clearReconnectTimeout]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    clearReconnectTimeout();
    reconnectCountRef.current = 0;

    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }

    setStatus('disconnected');
    setClientId(null);
  }, [clearReconnectTimeout]);

  const subscribe = useCallback((newTopics: WebSocketTopic[]) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          topics: newTopics,
          action: 'subscribe',
        }),
      );
    }
  }, []);

  const unsubscribe = useCallback((topicsToRemove: WebSocketTopic[]) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          action: 'unsubscribe',
          topics: topicsToRemove,
        }),
      );
    }
  }, []);

  const send = useCallback((message: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    let connectTimeout: ReturnType<typeof setTimeout> | null = null;

    if (autoConnect) {
      // Small delay to handle React StrictMode double-mount
      connectTimeout = setTimeout(() => {
        if (isMountedRef.current) {
          connectRef.current();
        }
      }, 0);
    }

    return () => {
      isMountedRef.current = false;
      shouldReconnectRef.current = false;

      if (connectTimeout) {
        clearTimeout(connectTimeout);
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (wsRef.current) {
        const ws = wsRef.current;
        wsRef.current = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.onopen = null;
        ws.onmessage = null;

        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000, 'Component unmount');
        }
      }
    };
  }, [autoConnect]);

  // Disconnect when session expires, reconnect when restored
  const sessionExpired = useAuthStore((s) => s.sessionExpired);
  const prevSessionExpiredRef = useRef(sessionExpired);

  useEffect(() => {
    const wasExpired = prevSessionExpiredRef.current;
    prevSessionExpiredRef.current = sessionExpired;

    if (sessionExpired) {
      shouldReconnectRef.current = false;
      clearReconnectTimeout();

      if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
        wsRef.current.close(1000, 'Session expired');
      }
    } else if (wasExpired && isMountedRef.current && autoConnect) {
      // Only reconnect on transition from expired → active (not on initial mount)
      shouldReconnectRef.current = true;
      reconnectCountRef.current = 0;
      connectRef.current();
    }
  }, [sessionExpired, autoConnect, clearReconnectTimeout]);

  return {
    send,
    status,
    connect,
    clientId,
    subscribe,
    disconnect,
    unsubscribe,
    isConnected: status === 'connected',
  };
};
